import { useStoreState } from 'easy-peasy';
import { PropTypes } from 'prop-types';
import React, { useContext, useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Loader } from '@ge/components/loader';
import { ScrollingContainer } from '@ge/components/scrolling-container';
import { useColumnState } from '@ge/components/table/use-column-state';
import { Tab, Tabs } from '@ge/components/tabs';
import { useAssetDamageDetails } from '@ge/feat-inspections/data-hooks/use-asset-damage-details';
import {
  Capability,
  EntityType,
  PermissionScope,
  AuthStrategy,
  EntityTab,
  DamgesStatus,
  DamgesComponent,
  SortDirection,
  MonitorDefs,
} from '@ge/models/constants';
import { EntityDetailsContext } from '@ge/shared/context/entity-details-context';
import { useAuth } from '@ge/shared/data-hooks';
import {
  CaseDamagesColumnDefs,
  defaultCaseDamagesCols,
} from '@ge/shared/models/table-col-defs/case-damage-table-cols';
import { killEventPropagation } from '@ge/shared/util';
import { sorter } from '@ge/util/metric-sorter';

import NotesAttachments from '../components/notes-attachments';
import { DetailContainer, SectionContainer } from '../entity-details-shared';

import AssetDetailsHeader from './asset-details-header';
// Hiding for MVP0 - Story #533
import { AssetHistory } from './panels/asset-history';
import { AssetInfo } from './panels/asset-info';
import { AssetPerformance } from './panels/asset-performance';
import { AssetRealtimeInfo } from './panels/asset-realtime-info';

const StyledTabs = styled(Tabs)`
  margin-top: 20px;
  min-height: 350px !important;
`;

export const AssetDamageMetric = {
  CREATION_DATE: 'creationDate',
};

const AssetDetails = ({
  assetId,
  entityType,
  entityTab,
  handleNewTask,
  setShowLinkModal,
  isDatePersist,
  popsout,
}) => {
  const { t, ready } = useTranslation(['entity-details'], {
    useSuspense: false,
  });

  const { manageAssetTabs, showDamageDetails, resetDetails, setIsAssetPanel } =
    useContext(EntityDetailsContext);
  const [filterDefs, setFilterDefs] = useState({});
  const [currentSearch, setCurrentSearch] = useState({});
  const [filteredAssetDamages, setFilteredAssetDamages] = useState([]);
  const [filteredHighestCategory, setFilteredHighestCategory] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isIconClicked, setIsIconClicked] = useState(false);

  // // Commenting as we are not showing events for now
  // // const { fetchAssetEvents } = useStoreActions((actions) => actions.assets);

  // // Fetch relevant data.
  // useEffect(() => {
  //   fetchAssetEvents(assetId);
  // }, [fetchAssetEvents, assetId]);
  const getAssetById = useStoreState((state) => state.assets.getAssetById);
  const assetDetails = getAssetById(assetId);
  const { isAuthorized } = useAuth();

  const isAuthorizedToViewRealTimeInfo = isAuthorized({
    capabilities: [
      { capability: Capability.REAL_TIME_PAGES, scopes: [PermissionScope.VIEW] },
      { capability: Capability.CASES, scopes: [PermissionScope.VIEW] },
      { capability: Capability.CASES_RECOMMENDATIONS, scopes: [PermissionScope.VIEW] },
      { capability: Capability.FIELD_TASKS, scopes: [PermissionScope.VIEW] },
    ],
  });

  const isAuthorizedToViewAssetHistory = isAuthorized({
    capabilities: [
      { capability: Capability.CASES_RECOMMENDATIONS, scopes: [PermissionScope.VIEW] },
      { capability: Capability.FIELD_TASKS, scopes: [PermissionScope.VIEW] },
      { capability: Capability.CASES, scopes: [PermissionScope.VIEW] },
    ],
    siteIds: [assetDetails?.site?.id],
  });

  const isAuthorizedToViewPerformance = isAuthorized({
    capabilities: [{ capability: Capability.CORE_KPIS, scopes: [PermissionScope.VIEW] }],
  });

  const isAuthorizedToViewNotes = isAuthorized({
    capabilities: [
      { capability: Capability.NOTES_AND_ATTACHMENTS, scopes: [PermissionScope.VIEW] },
    ],
  });

  const isAuthorizedToViewAssetInfo = isAuthorized({
    capabilities: [{ capability: Capability.ASSET_MANAGEMENT, scopes: [PermissionScope.VIEW] }],
    siteIds: [assetDetails?.site?.id],
  });

  const isAuthorizedToViewTasks = isAuthorized({
    capabilities: [{ capability: Capability.FIELD_TASKS, scopes: [PermissionScope.VIEW] }],
    siteIds: [assetDetails?.site?.id],
  });
  const isAuthorizedToMonitorCases = isAuthorized({
    capabilities: [{ capability: Capability.CASES, scopes: [PermissionScope.VIEW] }],
    siteIds: [assetDetails?.site?.id],
  });
  const isAuthorizedToAnalyzeCases = isAuthorized({
    capabilities: [
      { capability: Capability.CASES_RECOMMENDATIONS, scopes: [PermissionScope.VIEW] },
    ],
    siteIds: [assetDetails?.site?.id],
  });
  const isAuthorizedAlertsHistory = isAuthorized({
    capabilities: [{ capability: Capability.ALERTS, scopes: [PermissionScope.VIEW] }],
    siteIds: [assetDetails?.site?.id],
  });

  const isAuthorizedToViewSIs = isAuthorized({
    capabilities: [{ capability: Capability.SPECIAL_INSTRUCTION, scopes: [PermissionScope.VIEW] }],
  });

  let defaultTab = entityTab;

  if (!defaultTab || defaultTab === null) {
    if (isAuthorizedToViewRealTimeInfo) {
      defaultTab = EntityTab.REALTIME_INFO;
    }
    if (isAuthorizedToViewAssetHistory) {
      defaultTab = EntityTab.HISTORY;
    }
    if (isAuthorizedToViewPerformance) {
      defaultTab = EntityTab.PERFORMANCE;
    }
    if (isAuthorizedToViewNotes) {
      defaultTab = EntityTab.NOTES_AND_ATTACHMENTS;
    }
  }

  const handleEntityTab = useCallback(
    (e) => {
      manageAssetTabs(assetId, e, entityType);
    },
    [assetId, manageAssetTabs, entityType],
  );

  const { sortDirection, sortMetric, updateSortMetric } = useColumnState({
    columnDefs: CaseDamagesColumnDefs,
    defaultCols: defaultCaseDamagesCols,
    defaultSortMetric: AssetDamageMetric.CREATION_DATE,
    defaultSortDirection: SortDirection.ASC,
    sortStateId: MonitorDefs.ASSET_DAMAGES_FILTER_STATE_ID,
  });

  const {
    data: assetDamageData,
    isLoading,
    filterValues,
  } = useAssetDamageDetails({
    assetId: assetId,
    filters: filterDefs,
    search: currentSearch,
    sortMetric,
    sortDirection,
    isAuthorizedToViewAssetDamage: isAuthorizedToViewPerformance,
  });

  const handleFilter = useCallback(
    (updateDefs) => {
      setFilterDefs(updateDefs);
    },
    [setFilterDefs],
  );

  const handleFilterChange = useCallback((search) => setCurrentSearch(search), [setCurrentSearch]);

  const getHighestCategory = useCallback((damages) => {
    const sortedData = damages?.sort(sorter('category', SortDirection.DESC)) ?? [];
    return sortedData[0];
  }, []);

  useEffect(() => {
    if (assetDamageData?.length) {
      const filteredData = assetDamageData?.filter((damage) => {
        return DamgesStatus.CATEGORY.includes(damage?.category);
      });
      setFilteredAssetDamages(filteredData);
    }
  }, [assetDamageData]);

  useEffect(() => {
    if (filterDefs) {
      const filterApplied =
        filterDefs && Object.values(filterDefs)?.some((value) => value?.value?.length > 0);
      if (filterApplied) {
        setIsFilterApplied(true);
      } else {
        setIsFilterApplied(false);
      }
    }
  }, [filterDefs]);

  useEffect(() => {
    if (filteredAssetDamages?.length > 0 && !isFilterApplied) {
      let category = [];
      const filteredMainBearingData = filteredAssetDamages?.filter((damage) => {
        return DamgesComponent.main_bearing === damage?.component && damage?.status === 'Open';
      });

      const highestMainBearingCategory = getHighestCategory(filteredMainBearingData);
      category.push(highestMainBearingCategory);

      const filteredGearboxData = filteredAssetDamages?.filter((damage) => {
        return DamgesComponent.gearbox === damage?.component && damage?.status === 'Open';
      });

      const highestGearboxCategory = getHighestCategory(filteredGearboxData);
      category.push(highestGearboxCategory);

      const filteredGeneratorData = filteredAssetDamages?.filter((damage) => {
        return DamgesComponent.generator === damage?.component && damage?.status === 'Open';
      });

      const highestGeneratorCategory = getHighestCategory(filteredGeneratorData);
      category.push(highestGeneratorCategory);

      const filteredPitchBearingData = filteredAssetDamages?.filter((damage) => {
        return DamgesComponent.pitch_bearing === damage?.component && damage?.status === 'Open';
      });

      const highestPitchBearingCategory = getHighestCategory(filteredPitchBearingData);
      category.push(highestPitchBearingCategory);
      setFilteredHighestCategory(category);
    }
  }, [filteredAssetDamages, getHighestCategory, isFilterApplied]);

  useEffect(() => {
    if (isIconClicked && defaultTab !== EntityTab.INFO) {
      manageAssetTabs(assetId, EntityTab.INFO, entityType);
      setIsIconClicked(false);
    } else {
      setIsIconClicked(false);
    }
  }, [assetId, defaultTab, entityType, isIconClicked, manageAssetTabs]);

  const handleSelectDamage = useCallback(
    (e, id) => {
      killEventPropagation(e);
      e.preventDefault();
      resetDetails();
      setIsAssetPanel(true);
      showDamageDetails(id);
    },
    [resetDetails, setIsAssetPanel, showDamageDetails],
  );

  if (!ready) {
    return <Loader />;
  }

  return (
    <DetailContainer>
      <AssetDetailsHeader
        assetId={assetId}
        entityType={entityType}
        ComponentCategory={filteredHighestCategory}
        setIsIconClicked={setIsIconClicked}
      />
      <ScrollingContainer autoHide={false}>
        <SectionContainer>
          <StyledTabs small defaultTab={defaultTab} onChangeHandler={handleEntityTab}>
            <Tab
              capabilities={[
                { capability: Capability.REAL_TIME_PAGES, scopes: [PermissionScope.VIEW] },
                { capability: Capability.CASES, scopes: [PermissionScope.VIEW] },
                { capability: Capability.CASES_RECOMMENDATIONS, scopes: [PermissionScope.VIEW] },
                { capability: Capability.FIELD_TASKS, scopes: [PermissionScope.VIEW] },
              ]}
              small
              label={t('tabs.realtime_info', 'Realtime Info')}
            >
              {isAuthorizedToViewRealTimeInfo ? (
                <AssetRealtimeInfo
                  assetId={assetId}
                  handleNewTask={handleNewTask}
                  setShowLinkModal={setShowLinkModal}
                />
              ) : (
                ''
              )}
            </Tab>
            <Tab
              capabilities={[
                { capability: Capability.CASES_RECOMMENDATIONS, scopes: [PermissionScope.VIEW] },
                { capability: Capability.FIELD_TASKS, scopes: [PermissionScope.VIEW] },
                { capability: Capability.CASES, scopes: [PermissionScope.VIEW] },
              ]}
              authStrategy={AuthStrategy.ANY}
              label={t('tabs.history', 'History')}
            >
              {isAuthorizedToViewAssetHistory ? (
                <AssetHistory
                  assetId={assetId}
                  isAuthorizedToViewTasks={isAuthorizedToViewTasks}
                  isAuthorizedToMonitorCases={isAuthorizedToMonitorCases}
                  isAuthorizedToAnalyzeCases={isAuthorizedToAnalyzeCases}
                  isAuthorizedAlertsHistory={isAuthorizedAlertsHistory}
                />
              ) : (
                ''
              )}
            </Tab>
            <Tab
              capabilities={[{ capability: Capability.CORE_KPIS, scopes: [PermissionScope.VIEW] }]}
              label={t('tabs.performance', 'Performance')}
            >
              {isAuthorizedToViewPerformance ? (
                <AssetPerformance
                  assetId={assetId}
                  isDatePersist={isDatePersist}
                  popsout={popsout}
                />
              ) : (
                ''
              )}
            </Tab>
            <Tab
              capabilities={[
                { capability: Capability.NOTES_AND_ATTACHMENTS, scopes: [PermissionScope.VIEW] },
              ]}
              label={t('tabs.notes', 'Notes')}
            >
              {isAuthorizedToViewNotes ? (
                <NotesAttachments
                  entity={{ id: assetId }}
                  entityType={EntityType.ASSET}
                  siteType={entityType}
                />
              ) : (
                ''
              )}
            </Tab>
            <Tab
              capabilities={[
                { capability: Capability.ASSET_MANAGEMENT, scopes: [PermissionScope.VIEW] },
              ]}
              label={t('tabs.info', 'Info')}
            >
              {isAuthorizedToViewAssetInfo ? (
                <AssetInfo
                  entity={{ id: assetId }}
                  entityType={EntityType.ASSET}
                  isMonitor={isAuthorizedToViewSIs}
                  assetDamages={assetDamageData}
                  isLoading={isLoading}
                  sortAction={updateSortMetric}
                  sortMetric={sortMetric}
                  sortDirection={sortDirection}
                  onFilter={handleFilter}
                  onFilterChange={handleFilterChange}
                  filterValues={filterValues}
                  handleSelectDamage={handleSelectDamage}
                  isAuthorizedToViewAssetDamage={isAuthorizedToViewPerformance}
                />
              ) : (
                ''
              )}
            </Tab>
            {/* <Tab label={t('tabs.performance', 'Performance')}>
              {site && <AssetPerformance asset={asset} />}
            </Tab>
            <Tab label={t('tabs.events', 'Events')}>
              <div>Events Content</div>
            </Tab>
            <Tab label={t('tabs.operations', 'Operations')}>
              <div>Operations Content</div>
            </Tab> */}
          </StyledTabs>
        </SectionContainer>
      </ScrollingContainer>
    </DetailContainer>
  );
};

AssetDetails.propTypes = {
  assetId: PropTypes.string.isRequired,
  entityType: PropTypes.string,
  entityTab: PropTypes.string,
  setCaseId: PropTypes.any,
  caseId: PropTypes.string,
  handleNewTask: PropTypes.func,
  setShowLinkModal: PropTypes.func,
  isDatePersist: PropTypes.bool,
  popsout: PropTypes.bool,
};

export default AssetDetails;
